import React, { useRef, useEffect, useCallback } from 'react';

const MAX_PIXEL = 20;
const MIN_PIXEL = 1;

function applyChromatic(ctx, w, h, offsetX) {
  if (!offsetX) return;
  const imageData = ctx.getImageData(0, 0, w, h);
  const src = new Uint8ClampedArray(imageData.data);
  const d = imageData.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const rx = Math.min(w - 1, x + offsetX);
      const bx = Math.max(0, x - offsetX);
      d[i]     = src[(y * w + rx) * 4];      // R desplazado derecha
      d[i + 2] = src[(y * w + bx) * 4 + 2];  // B desplazado izquierda
    }
  }
  ctx.putImageData(imageData, 0, 0);
}

function drawCover(ctx, img, w, h) {
  const ir = img.naturalWidth / img.naturalHeight;
  const cr = w / h;
  let dw, dh, ox, oy;
  if (ir > cr) {
    dh = h; dw = dh * ir;
    ox = 0; oy = 0; // alineado a la izquierda
  } else {
    dw = w; dh = dw / ir;
    ox = 0; oy = (h - dh) / 2;
  }
  ctx.drawImage(img, ox, oy, dw, dh);
}

function drawCoverPixelated(ctx, offscreen, img, w, h, ps) {
  const offCtx = offscreen.getContext('2d');
  const offW = Math.max(1, Math.ceil(w / ps));
  const offH = Math.max(1, Math.ceil(h / ps));
  offscreen.width = offW;
  offscreen.height = offH;

  const ir = img.naturalWidth / img.naturalHeight;
  const cr = offW / offH;
  let dw, dh, ox, oy;
  if (ir > cr) {
    dh = offH; dw = dh * ir;
    ox = 0; oy = 0; // alineado a la izquierda
  } else {
    dw = offW; dh = dw / ir;
    ox = 0; oy = (offH - dh) / 2;
  }
  offCtx.drawImage(img, ox, oy, dw, dh);

  ctx.clearRect(0, 0, w, h);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(offscreen, 0, 0, offW, offH, 0, 0, w, h);
}

const PixelateHover = ({ src, className, style }) => {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(document.createElement('canvas'));
  const imgRef = useRef(null);
  const animRef = useRef(null);
  const pixelSizeRef = useRef(MIN_PIXEL);
  const hoveredRef = useRef(false);
  const intervalRef = useRef(null);
  const chromaticRef = useRef(0);
  const loadedRef = useRef(false);

  const draw = useCallback((ps) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !loadedRef.current) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (ps <= 1.2) {
      ctx.imageSmoothingEnabled = true;
      drawCover(ctx, img, w, h);
    } else {
      drawCoverPixelated(ctx, offscreenRef.current, img, w, h, ps);
    }
    if (chromaticRef.current) applyChromatic(ctx, w, h, chromaticRef.current);
  }, []);

  const fireGlitch = useCallback(() => {
    if (hoveredRef.current) return;
    const levels = [2, 4, 8, 14, 20];
    // Ráfaga: 3-5 cambios rápidos
    const steps = 3 + Math.floor(Math.random() * 3);
    let i = 0;
    const burst = () => {
      if (hoveredRef.current) { draw(MIN_PIXEL); return; }
      if (i < steps) {
        const ps = levels[Math.floor(Math.random() * levels.length)];
        // Aberración cromática aleatoria en algunos frames
        chromaticRef.current = Math.random() > 0.4 ? Math.floor(4 + Math.random() * 20) : 0;
        pixelSizeRef.current = ps;
        draw(ps);
        i++;
        setTimeout(burst, 40 + Math.random() * 60);
      } else {
        chromaticRef.current = 0;
        pixelSizeRef.current = MIN_PIXEL;
        draw(MIN_PIXEL);
      }
    };
    burst();
  }, [draw]);

  const startIdleInterval = useCallback(() => {
    const scheduleNext = () => {
      if (hoveredRef.current) return;
      intervalRef.current = setTimeout(() => {
        fireGlitch();
        scheduleNext();
      }, 1500 + Math.random() * 2500);
    };
    scheduleNext();
  }, [fireGlitch]);

  const stopIdleInterval = useCallback(() => {
    clearTimeout(intervalRef.current);
  }, []);

  const loopIdle = startIdleInterval;

  // Transición suave hacia nitidez al hover
  const animateToSharp = useCallback(() => {
    pixelSizeRef.current += (MIN_PIXEL - pixelSizeRef.current) * 0.2;
    draw(pixelSizeRef.current);
    if (Math.abs(pixelSizeRef.current - MIN_PIXEL) > 0.15) {
      animRef.current = requestAnimationFrame(animateToSharp);
    } else {
      pixelSizeRef.current = MIN_PIXEL;
      draw(MIN_PIXEL);
    }
  }, [draw]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !loadedRef.current) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    draw(pixelSizeRef.current);
  }, [draw]);

  useEffect(() => {
    const img = new Image();
    imgRef.current = img;
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => {
      loadedRef.current = true;
      resizeCanvas();
      startIdleInterval();
    };

    const ro = new ResizeObserver(resizeCanvas);
    if (canvasRef.current) ro.observe(canvasRef.current);

    return () => {
      cancelAnimationFrame(animRef.current);
      clearTimeout(intervalRef.current);
      ro.disconnect();
    };
  }, [src, startIdleInterval, resizeCanvas]);

  const handleMouseEnter = () => {
    hoveredRef.current = true;
    stopIdleInterval();
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(animateToSharp);
  };

  const handleMouseLeave = () => {
    hoveredRef.current = false;
    cancelAnimationFrame(animRef.current);
    startIdleInterval();
  };

  return (
    <div
      className={className}
      style={{ position: 'relative', overflow: 'hidden', ...style }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
      />
    </div>
  );
};

export default PixelateHover;
